import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../lib/api';
import { formatEuro, formatMois } from '../lib/format';
import { Modal } from '../components/ui/Modal';
import type { CashflowMonth } from '@cockpit/shared';

export function TresorerieCashflowPage(): JSX.Element {
  const [months, setMonths] = useState(6);
  const [includePipeline, setIncludePipeline] = useState(true);
  const [detail, setDetail] = useState<{
    month: CashflowMonth;
    kind: 'entrees' | 'sorties';
  } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['cashflow', { months, includePipeline }],
    queryFn: () =>
      api.tresorerie.cashflow({
        months,
        include_pipeline: includePipeline ? 'true' : 'false',
      }),
  });

  return (
    <div className="max-w-7xl space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-slate-100">Cash flow prévisionnel</h1>
        <p className="text-xs text-slate-500 mt-1">
          Entrées (échéances + pipeline pondéré) et sorties (Global Wood, containers, charges,
          remboursements) sur les prochains mois. Point de départ : solde 0 (référence neutre).
        </p>
      </header>

      <div className="flex flex-wrap gap-4 items-center">
        <label className="inline-flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={includePipeline}
            onChange={(e): void => setIncludePipeline(e.target.checked)}
            className="rounded border-slate-600 bg-slate-800"
          />
          Inclure pipeline pondéré
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          Horizon&nbsp;:
          <input
            type="range"
            min="3"
            max="24"
            step="1"
            value={months}
            onChange={(e): void => setMonths(Number.parseInt(e.target.value, 10))}
            className="w-40"
          />
          <span className="font-mono text-slate-100 w-12 text-right">{months} mois</span>
        </label>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">Chargement…</p>
      ) : (
        <>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-200 mb-3">Solde cumulé prévisionnel</h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart
                data={(data?.months ?? []).map((m) => ({
                  mois: formatMois(m.mois),
                  solde_cumule: m.solde_cumule_centimes / 100,
                  solde_mois: m.solde_mois_centimes / 100,
                }))}
              >
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="mois" stroke="#64748b" tick={{ fontSize: 11 }} />
                <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    border: '1px solid #334155',
                    borderRadius: '0.5rem',
                  }}
                  formatter={(v: number): string =>
                    `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v)} €`
                  }
                />
                <ReferenceLine y={0} stroke="#475569" strokeDasharray="3 3" />
                <Line
                  type="monotone"
                  dataKey="solde_cumule"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#10b981' }}
                  name="Solde cumulé"
                />
                <Line
                  type="monotone"
                  dataKey="solde_mois"
                  stroke="#60a5fa"
                  strokeWidth={1.5}
                  dot={{ r: 3 }}
                  name="Solde du mois"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/50 border-b border-slate-800">
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                  <th className="py-2 px-3">Mois</th>
                  <th className="py-2 px-3 text-right">Échéances</th>
                  <th className="py-2 px-3 text-right">Pipeline</th>
                  <th className="py-2 px-3 text-right">Total entrées</th>
                  <th className="py-2 px-3 text-right">Global Wood</th>
                  <th className="py-2 px-3 text-right">Containers</th>
                  <th className="py-2 px-3 text-right">Charges</th>
                  <th className="py-2 px-3 text-right">Avoirs</th>
                  <th className="py-2 px-3 text-right">Total sorties</th>
                  <th className="py-2 px-3 text-right">Solde mois</th>
                  <th className="py-2 px-3 text-right">Cumulé</th>
                </tr>
              </thead>
              <tbody>
                {(data?.months ?? []).map((m) => (
                  <tr key={m.mois} className="border-b border-slate-800/50">
                    <td className="py-2 px-3">{formatMois(m.mois)}</td>
                    <td
                      className="py-2 px-3 text-right text-emerald-300 cursor-pointer hover:bg-slate-800/50"
                      onClick={(): void => setDetail({ month: m, kind: 'entrees' })}
                    >
                      {formatEuro(m.entrees.echeances_centimes)}
                    </td>
                    <td className="py-2 px-3 text-right text-emerald-300/70">
                      {formatEuro(m.entrees.pipeline_centimes)}
                    </td>
                    <td className="py-2 px-3 text-right font-medium text-emerald-300">
                      {formatEuro(m.entrees.total_centimes)}
                    </td>
                    <td className="py-2 px-3 text-right text-rose-300">
                      −{formatEuro(m.sorties.global_wood_centimes)}
                    </td>
                    <td
                      className="py-2 px-3 text-right text-rose-300 cursor-pointer hover:bg-slate-800/50"
                      onClick={(): void => setDetail({ month: m, kind: 'sorties' })}
                    >
                      −{formatEuro(m.sorties.containers_centimes)}
                    </td>
                    <td className="py-2 px-3 text-right text-rose-300">
                      −{formatEuro(m.sorties.charges_fixes_centimes)}
                    </td>
                    <td className="py-2 px-3 text-right text-rose-300">
                      −{formatEuro(m.sorties.avoirs_remboursement_centimes)}
                    </td>
                    <td className="py-2 px-3 text-right font-medium text-rose-300">
                      −{formatEuro(m.sorties.total_centimes)}
                    </td>
                    <td
                      className={`py-2 px-3 text-right font-semibold ${
                        m.solde_mois_centimes >= 0 ? 'text-emerald-300' : 'text-rose-300'
                      }`}
                    >
                      {m.solde_mois_centimes >= 0 ? '+' : ''}
                      {formatEuro(m.solde_mois_centimes)}
                    </td>
                    <td
                      className={`py-2 px-3 text-right font-semibold ${
                        m.solde_cumule_centimes >= 0 ? 'text-emerald-300' : 'text-rose-300'
                      }`}
                    >
                      {m.solde_cumule_centimes >= 0 ? '+' : ''}
                      {formatEuro(m.solde_cumule_centimes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {detail !== null && <DetailModal detail={detail} onClose={(): void => setDetail(null)} />}
        </>
      )}
    </div>
  );
}

function DetailModal({
  detail,
  onClose,
}: {
  detail: { month: CashflowMonth; kind: 'entrees' | 'sorties' };
  onClose: () => void;
}): JSX.Element {
  const { month, kind } = detail;
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`${kind === 'entrees' ? 'Entrées' : 'Sorties'} — ${formatMois(month.mois)}`}
    >
      {kind === 'entrees' ? (
        <div className="space-y-3">
          <Section title="Échéances clients">
            {month.entrees.details_echeances.length === 0 ? (
              <p className="text-xs text-slate-500">Aucune.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {month.entrees.details_echeances.map((e) => (
                  <li key={e.vente_id} className="flex justify-between gap-3">
                    <span className="text-slate-300">
                      {e.contact_nom} — {e.numero_facture_henrri ?? '#' + String(e.vente_id)}
                    </span>
                    <span className="text-emerald-300 font-mono">
                      {formatEuro(e.montant_attendu_centimes)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <Section title="Pipeline pondéré">
            {month.entrees.details_pipeline.length === 0 ? (
              <p className="text-xs text-slate-500">Aucun.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {month.entrees.details_pipeline.map((p) => (
                  <li
                    key={`${String(p.contact_id)}-${p.statut_pipeline}`}
                    className="flex justify-between gap-3"
                  >
                    <span className="text-slate-300">
                      {p.contact_societe ?? p.contact_nom} ·{' '}
                      <span className="text-slate-500">
                        {p.statut_pipeline} (×{(p.poids * 100).toFixed(0)}%)
                      </span>
                    </span>
                    <span className="text-emerald-300/70 font-mono">
                      {formatEuro(p.montant_pondere_centimes)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      ) : (
        <div className="space-y-3">
          <Section title="Containers">
            {month.sorties.details_containers.length === 0 ? (
              <p className="text-xs text-slate-500">Aucun.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {month.sorties.details_containers.map((c) => (
                  <li key={c.container_id} className="flex justify-between gap-3">
                    <span className="text-slate-300">{c.reference_container}</span>
                    <span className="text-rose-300 font-mono">
                      −{formatEuro(c.cout_total_eur_centimes)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <Section title="Charges fixes">
            <ul className="space-y-1 text-sm">
              {month.sorties.details_charges.map((c) => (
                <li key={c.charge_id} className="flex justify-between gap-3">
                  <span className="text-slate-300">
                    {c.libelle} (jour {c.jour_du_mois})
                  </span>
                  <span className="text-rose-300 font-mono">−{formatEuro(c.montant_centimes)}</span>
                </li>
              ))}
            </ul>
          </Section>
          <Section title="Remboursements (avoirs)">
            {month.sorties.details_avoirs.length === 0 ? (
              <p className="text-xs text-slate-500">Aucun.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {month.sorties.details_avoirs.map((a) => (
                  <li key={a.avoir_id} className="flex justify-between gap-3">
                    <span className="text-slate-300">
                      {a.numero_avoir_henrri ?? `Avoir #${String(a.avoir_id)}`}
                    </span>
                    <span className="text-rose-300 font-mono">
                      −{formatEuro(a.montant_centimes)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      )}
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">{title}</h4>
      {children}
    </div>
  );
}
